/* global google */

export default class InteractiveMap {
    constructor(el, app) {
        this.el = el;
        this.app = app;

        this.mapEl = this.el.querySelector('.js-map');
        this.dealerEls = this.el.querySelectorAll('.js-dealer');
        this.markerId = new Map();

        this.APIKey = 'AIzaSyDYxGDYNtKcKDfGDL3cBpDgm8kr_1PRJ2E';
        this.mapId = '9cddd46fc8a150a4ab490fb3';

        this.markers = [];
        this.dealerships = [];

        // Simple state management
        this.activeDealerId = null;
        this.activeMarker = null;
        this.activeDealerEl = null;

        // Parse dealership data from data attribute
        this.parseDealershipData();

        // Load Google Maps API if not already loaded
        if (!window.google || !window.google.maps) {
            this.loadGoogleMapsAPI();
        } else {
            this.initMap();
        }
    }

    parseDealershipData() {
        try {
            const dealershipData = this.el.getAttribute('data-dealerships');

            if (!dealershipData) {
                this.dealerships = [];
                return;
            }

            const parsed = JSON.parse(dealershipData);
            this.dealerships = this.normalizeDealerships(parsed);
        } catch (error) {
            console.error('Error parsing dealership data:', error);
            this.dealerships = [];
        }
    }

    normalizeDealerships(data) {
        // Si c'est déjà un tableau → on retourne tel quel
        if (Array.isArray(data)) {
            return data;
        }

        // Si c'est un objet groupé (ex: provinces)
        if (typeof data === 'object' && data !== null) {
            return Object.values(data).flat();
        }

        // Sinon → fallback sécurité
        return [];
    }

    loadGoogleMapsAPI() {
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${this.APIKey}&libraries=marker&callback=initGoogleMap&loading=async`;
        script.async = true;
        script.defer = true;

        script.onerror = () => {
            console.error('Failed to load Google Maps API');
            this.el.innerHTML = `
                <div style="height: 100%; display: flex; align-items: center; justify-content: center; text-align: center; padding: 20px;">
                    <p>Unable to load map. Please check your internet connection.</p>
                </div>
            `;
        };

        window.initGoogleMap = () => {
            this.initMap();
        };

        document.head.appendChild(script);
    }

    initMap() {
        this.map = new google.maps.Map(this.mapEl, {
            center: { lat: 44.06259621348269, lng: -94.02857365529778 },
            zoom: 5,
            mapId: this.mapId,
            zoomControl: true,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false,
            clickableIcons: false,
        });

        this.geocoder = new google.maps.Geocoder();
        this.bounds = new google.maps.LatLngBounds();
        this._pendingGeocode = 0;

        if (this.dealerships.length) {
            this.addMarkers();
            this.bindDealerClicks();
        }
    }

    bindDealerClicks() {
        this.dealerEls.forEach((dealerEl) => {
            dealerEl.addEventListener('click', () => {
                const dealerId = dealerEl.dataset.dealerId;
                if (dealerId) {
                    this.activateDealer(dealerId);
                }
            });

            // Add hover effects synchronized with pins
            dealerEl.addEventListener('mouseenter', () => {
                const dealerId = dealerEl.dataset.dealerId;
                const marker = this.markerId.get(dealerId);
                if (marker && !marker.content.classList.contains('is-active')) {
                    marker.content.style.transform = 'scale(1.15)';
                    marker.content.style.transition = 'transform 0.2s ease';
                }
            });

            dealerEl.addEventListener('mouseleave', () => {
                const dealerId = dealerEl.dataset.dealerId;
                const marker = this.markerId.get(dealerId);
                if (marker && !marker.content.classList.contains('is-active')) {
                    marker.content.style.transform = 'scale(1)';
                }
            });
        });
    }

    addMarkers() {
        let hasAsyncMarkers = false;

        this.dealerships.forEach((dealership) => {
            const hasCoords =
                dealership.latitude != null &&
                dealership.latitude !== '' &&
                dealership.longitude != null &&
                dealership.longitude !== '';
            if (hasCoords) {
                this.addMarker(dealership);
            } else if (dealership.address) {
                hasAsyncMarkers = true;
                this._pendingGeocode++;
                this.geocodeAddress(dealership);
            }
        });

        if (!hasAsyncMarkers) {
            this.fitMapToMarkers();
        }
    }

    fitMapToMarkers() {
        if (this.markers.length === 0) return;

        this.bounds = new google.maps.LatLngBounds();

        this.markers.forEach((marker) => {
            this.bounds.extend(marker.position);
        });

        this.map.fitBounds(this.bounds, {
            top: 50,
            right: 50,
            bottom: 50,
            left: 50,
        });

        const listener = google.maps.event.addListener(this.map, 'idle', () => {
            if (this.map.getZoom() > 15) {
                this.map.setZoom(15);
            }
            google.maps.event.removeListener(listener);
        });
    }

    geocodeAddress(dealership) {
        this.geocoder.geocode({ address: dealership.address }, (results, status) => {
            this._pendingGeocode--;
            if (status === 'OK' && results[0]) {
                const googlePos = results[0].geometry.location;
                this.addMarker({
                    ...dealership,
                    latitude: googlePos.lat(),
                    longitude: googlePos.lng(),
                });
            } else {
                console.error(
                    `Geocoding failed for address: ${dealership.address}. Status: ${status}`
                );
            }

            if (this._pendingGeocode === 0) {
                this.fitMapToMarkers();
            }
        });
    }

    addMarker(dealership) {
        const position = {
            lat: parseFloat(dealership.latitude),
            lng: parseFloat(dealership.longitude),
        };

        const pinElement = document.createElement('div');

        pinElement.innerHTML = `
            <svg width="27" height="34" viewBox="0 0 27 34">
                <path d="M13.5 0C6 0 0 6 0 13.5C0 23 13.5 34 13.5 34C13.5 34 27 23 27 13.5C27 6 21 0 13.5 0Z" fill="black"/>
                <circle cx="13.5" cy="13.5" r="4" fill="white"/>
            </svg>
        `;

        pinElement.style.cursor = 'pointer';
        pinElement.style.padding = '5px';
        pinElement.style.display = 'flex';
        pinElement.style.alignItems = 'center';
        pinElement.style.justifyContent = 'center';

        const marker = new google.maps.marker.AdvancedMarkerElement({
            map: this.map,
            position: position,
            title: dealership.title,
            content: pinElement,
        });

        marker.dealerId = dealership.id;
        this.markerId.set(String(dealership.id), marker);

        marker.addEventListener('gmp-click', () => {
            this.activateDealer(dealership.id);
        });

        pinElement.addEventListener('click', () => {
            this.activateDealer(dealership.id);
        });

        pinElement.addEventListener('mouseenter', () => {
            pinElement.style.transform = 'scale(1.15)';
            pinElement.style.transition = 'transform 0.2s ease';
        });

        pinElement.addEventListener('mouseleave', () => {
            pinElement.style.transform = 'scale(1)';
        });

        this.markers.push(marker);
        this.bounds.extend(position);
    }

    activateDealer(dealerId) {
        dealerId = String(dealerId);

        // Prevent re-activation of the same dealer
        if (this.activeDealerId === dealerId) {
            return;
        }

        // Clear previous active states
        if (this.activeDealerEl) {
            this.activeDealerEl.classList.remove('is-active');
        }

        if (this.activeMarker) {
            this.activeMarker.content.classList.remove('is-active');
        }

        // Find dealer element and marker
        const dealerEl = this.el.querySelector(`.js-dealer[data-dealer-id="${dealerId}"]`);
        const marker = this.markerId.get(dealerId);

        if (!dealerEl || !marker) {
            console.warn(`Dealer or marker not found for ID: ${dealerId}`);
            return;
        }

        // Set new active states
        if (window.innerWidth >= 1024) {
            dealerEl.classList.add('is-active');
        }
        marker.content.classList.add('is-active');

        // Update map
        this.panThenZoom(marker.position, 10);

        // Scroll list item into view (only on desktop 1024px+)
        if (window.innerWidth >= 1024) {
            dealerEl.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
            });
        }

        // Update state
        this.activeDealerId = dealerId;
        this.activeMarker = marker;
        this.activeDealerEl = dealerEl;
    }

    panThenZoom(position, targetZoom) {
        // Lance le pan (déjà animé par Google)
        this.map.panTo(position);

        // Attendre que le pan soit terminé
        google.maps.event.addListenerOnce(this.map, 'idle', () => {
            this.smoothZoom(targetZoom);
        });
    }

    smoothZoom(targetZoom) {
        const startZoom = this.map.getZoom();
        const duration = 400;
        const startTime = performance.now();
        const zoomDiff = targetZoom - startZoom;

        const animate = (time) => {
            const progress = Math.min((time - startTime) / duration, 1);
            const eased = this.easeOutCubic(progress);
            const newZoom = startZoom + zoomDiff * eased;

            this.map.setZoom(newZoom);

            if (progress < 1) {
                requestAnimationFrame(animate);
            } else {
                this.map.setZoom(targetZoom);
            }
        };

        requestAnimationFrame(animate);
    }
    easeOutCubic(t) {
        return 1 - Math.pow(1 - t, 3);
    }
}
